import React from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPercent,
  faLandmark,
  faBell,
  faGift,
  faCalendar,
  faCreditCard,
  faShield,
  faHeart,
  faStar,
  faCircleInfo,
  faTriangleExclamation,
  faCircleCheck,
  faTag,
  faMoneyBill,
  faBadgePercent,
  faHouse
} from "@fortawesome/pro-regular-svg-icons";

// Map icon names to Font Awesome icon objects
const NOTIFICATION_CENTER_ICONS = {
  percent: faPercent,
  landmark: faLandmark,
  bell: faBell,
  gift: faGift,
  calendar: faCalendar,
  "credit-card": faCreditCard,
  shield: faShield,
  heart: faHeart,
  star: faStar,
  "circle-info": faCircleInfo,
  "triangle-exclamation": faTriangleExclamation,
  "circle-check": faCircleCheck,
  tag: faTag,
  "money-bill": faMoneyBill,
  "badge-percent": faBadgePercent
};

// Mapping from state abbreviations to full state names
const STATE_NAMES = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia"
};

const PURPLE_100 = "#EBE4FF";
const BLUE_100 = "#D9E8FC";
const GOLD_100 = "#FFF0BA";
const RED_100 = "#FFE5DD";
const GREEN_100 = "#DCF2CB";

const PURPLE_200 = "#CDBCFF";
const BLUE_200 = "#B2CFFA";
const GOLD_200 = "#FCDE74";
const RED_200 = "#FFAAA5";
const GREEN_200 = "#B8DA96";

const Device = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Frame = styled.div`
  position: relative;
  width: 473px;
  height: 932px;
  z-index: 1;
  transform: scale(var(--device-scale, 1));
  transform-origin: center center;
`;

const FrameOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("/images/simulator/device-frame.png");
  background-size: cover;
  z-index: 2;
  pointer-events: none;
`;

const Screen = styled.div`
  width: 393px;
  height: 852px;
  background-color: #000;
  position: absolute;
  top: 40px;
  left: 40px;
  z-index: 0;
  overflow: hidden;
  border-radius: 1rem;
`;

const Header = styled.div`
  width: 100%;
  height: 101px;
  background-image: url("/images/simulator/header.png");
  background-size: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
`;

const Tabs = styled.div`
  width: 100%;
  height: 84px;
  background-image: url("/images/simulator/tabs.png");
  background-size: cover;
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 2;
`;

const HomeIndicator = styled.div`
  width: 100%;
  height: 34px;
  position: absolute;
  bottom: 0;
  background-image: url("/images/simulator/home-indicator.png");
  background-size: contain;
  z-index: 99999;
`;

const HomeFeed = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background-color: #f5f4f3;
  position: absolute;
  top: 101px;
  left: 0;
  z-index: 1;
`;

const Wallet = styled.div`
  width: 100%;
  height: 192px;
  background-image: url("/images/simulator/wallet.png");
  background-size: contain;
`;

const Offers = styled.div`
  width: 100%;
  height: 520px;
  background-image: url("/images/simulator/offers.png");
  background-size: contain;
`;

const NotificationContainer = styled.div`
  position: absolute;
  bottom: 140px;
  left: 10px;
  right: 10px;
  z-index: 3;
  pointer-events: none;
`;

const NotificationBubble = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 23px;
`;

const NotificationAppIcon = styled.img`
  width: 38px;
  height: 38px;
  object-fit: contain;
  border-radius: 8px;
  flex-shrink: 0;
`;

const NotificationContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const NotificationHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const NotificationHeader = styled.div`
  font-weight: 600;
  z-index: 2;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const NotificationTimestamp = styled.div`
  font-weight: normal;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.5);
  flex-shrink: 0;
`;

const NotificationSubheader = styled.div`
  font-weight: normal;
  z-index: 2;
  color: #000;
`;

const NotificationScreen = styled.div`
  width: 100%;
  height: 100%;
  background-image: url("/images/simulator/notifications-screen.png");
  background-size: cover;
  background-position: center;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const NotificationCenterScreen = styled.div`
  width: 100%;
  height: 100%;
  background-color: #fff;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
`;

const NotificationCenterHeader = styled.div`
  width: 100%;
  height: 101px;
  background-image: url("/images/simulator/notifications-header.png");
  background-size: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
`;

const NotificationCenterList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  margin-top: 101px;
`;

const NotificationCenterItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 20px 16px;
  background-color: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationCenterIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${({ $color }) => {
    switch ($color) {
      case "purple":
        return PURPLE_100;
      case "blue":
        return BLUE_100;
      case "gold":
        return GOLD_100;
      case "red":
        return RED_100;
      case "green":
        return GREEN_100;
      default:
        return PURPLE_100;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const NotificationCenterItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const NotificationCenterItemTitle = styled.p`
  font-family: "Polymath for Propel Text";
  font-size: 16px;
  font-weight: 650;
  color: #000;
  line-height: 1.4;
  letter-spacing: 0.25px;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;

const NotificationCenterItemBody = styled.p`
  font-family: "Polymath for Propel Text";
  font-size: 16px;
  font-weight: 400;
  color: #000;
  line-height: 1.4;
  letter-spacing: 0.25px;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;

const NotificationCenterTimestamp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

const NotificationCenterTime = styled.span`
  font-family: "Polymath for Propel Text";
  font-size: 14px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.69);
  line-height: 1.4;
  letter-spacing: 0.25px;
  white-space: nowrap;
`;

const NotificationCenterUnreadDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #cd000c;
`;

const BannerContainer = styled.div`
  width: 100%;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  ${({ $color }) => {
    switch ($color) {
      case "green":
        return `
          border-top: 1px solid ${GREEN_200};
          border-bottom: 1px solid ${GREEN_200};
          background-color: ${GREEN_100};
        `;
      case "red":
        return `
          border-top: 1px solid ${RED_200};
          border-bottom: 1px solid ${RED_200};
          background-color: ${RED_100};
        `;
      case "gold":
        return `
          border-top: 1px solid ${GOLD_200};
          border-bottom: 1px solid ${GOLD_200};
          background-color: ${GOLD_100};
        `;
      default:
        return `
          border-top: 1px solid ${GREEN_200};
          border-bottom: 1px solid ${GREEN_200};
          background-color: ${GREEN_100};
        `;
    }
  }}
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BannerHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-direction: column;
  align-items: flex-start;
`;

const StateHeaderContainer = styled.div`
  color: #000;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: "Polymath for Propel Text";
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: 0.25px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
`;

const StateSeal = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: contain;
`;

const StateHeader = styled.span`
  color: #000;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: "Polymath for Propel Text";
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%; /* 14.4px */
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const BannerHeader = styled.h1`
  color: #000;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings: "ss02" on, "ss16" on, "cv14" on;
  font-family: "Grenette Pro";
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 125%; /* 25px */
`;

const BannerSubheader = styled.p`
  color: #000;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: "Polymath for Propel Text";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 22.4px */
  letter-spacing: 0.25px;
`;

const BannerButton = styled.button`
  outline: none;
  border: none;
  height: 40px;
  padding: 0 24px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 12px;
  background: #000;
  color: #fff;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: "Polymath for Propel Text";
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%; /* 22.4px */
  letter-spacing: 0.25px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BottomSheetOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 10;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const BottomSheetCard = styled.div`
  width: 100%;
  background-color: #fff;
  border-radius: 1rem 1rem 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const BottomSheetIllustrationHeader = styled.div`
  width: 100%;
  height: 164px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  ${({ $color }) => {
    switch ($color) {
      case "green":
        return `
          background-color: ${GREEN_200};
        `;
      case "red":
        return `
          background-color: ${RED_200};
        `;
      case "gold":
        return `
          background-color: ${GOLD_200};
        `;
      case "blue":
        return `
          background-color: ${BLUE_200};
        `;
      case "purple":
        return `
          background-color: ${PURPLE_200};
        `;
      default:
        return `
          background-color: ${GREEN_200};
        `;
    }
  }}
`;

const BottomSheetCloseButton = styled.img`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  cursor: pointer;
`;

const BottomSheetIllustration = styled.div`
  background-image: ${({ $illustration, $color }) =>
    `url("/images/simulator/illustrations/${$illustration}-${$color}.png")`};
  background-size: contain;
  background-repeat: no-repeat;

  ${({ $illustration }) => {
    switch ($illustration) {
      case "security":
        return `
          width: calc(282px / 2);
          height: calc(218px / 2);
          margin-left: 20px;
          margin-bottom: 4px;
        `;
      case "money":
        return `
          width: calc(292px / 2);
          height: calc(277px / 2);
          margin-top: 8px;
        `;
      case "weather":
        return `
          width: calc(356px / 2);
          height: calc(223px / 2);
          margin-bottom: 8px;
        `;
      default:
        return `
          width: calc(282px / 2);
          height: calc(217px / 2);
        `;
    }
  }}
`;

const BottomSheetContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  padding-bottom: 3rem;
`;

const BottomSheetHeader = styled.h2`
  color: #000;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings: "ss02" on, "ss16" on, "cv14" on;
  font-family: "Grenette Pro";
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 125%;
  margin: 0;
`;

const BottomSheetSubheader = styled.p`
  font-variant-numeric: lining-nums proportional-nums;
  font-family: "Polymath for Propel Text";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  letter-spacing: 0.25px;
  margin: 0;
`;

const BottomSheetButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const BottomSheetButton = styled.button`
  outline: none;
  border: none;
  height: 48px;
  margin-top: 8px;
  padding: 0 24px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 12px;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: "Polymath for Propel Text";
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  letter-spacing: 0.25px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ $variant }) => {
    switch ($variant) {
      case "primary":
        return `
          background: #000;
          color: #fff;
        `;
      case "secondary":
        return `
          background: #fff;
          color: #000;
        `;
      default:
        return `
          background: #000;
          color: #fff;
        `;
    }
  }}
`;

const BottomSheet = ({
  color,
  header,
  subheader,
  primaryButtonText,
  illustration
}) => {
  return (
    <BottomSheetOverlay>
      <BottomSheetCard>
        <BottomSheetIllustrationHeader $color={color}>
          <BottomSheetCloseButton
            src="/images/simulator/x-button.png"
            alt="Close"
          />
          <BottomSheetIllustration
            $illustration={illustration}
            $color={color}
          />
        </BottomSheetIllustrationHeader>
        <BottomSheetContent>
          <BottomSheetHeader>{header}</BottomSheetHeader>
          <BottomSheetSubheader>{subheader}</BottomSheetSubheader>
          <BottomSheetButtons>
            <BottomSheetButton $variant="primary">
              {primaryButtonText}
            </BottomSheetButton>
          </BottomSheetButtons>
        </BottomSheetContent>
      </BottomSheetCard>
    </BottomSheetOverlay>
  );
};

const Banner = ({ color, header, subheader, buttonText, state }) => {
  const stateSealUrl = state
    ? `https://us-state-seals.s3.us-east-1.amazonaws.com/${state}.svg`
    : null;

  return (
    <BannerContainer $color={color || "green"}>
      <BannerText>
        <BannerHeaderContainer>
          {stateSealUrl && (
            <StateHeaderContainer>
              <StateSeal
                src={stateSealUrl}
                alt={`${STATE_NAMES[state]} state seal`}
              />
              <StateHeader>
                Official news from State of {STATE_NAMES[state]}
              </StateHeader>
            </StateHeaderContainer>
          )}
          <BannerHeader>{header}</BannerHeader>
        </BannerHeaderContainer>
        <BannerSubheader>{subheader}</BannerSubheader>
      </BannerText>
      <BannerButton>{buttonText}</BannerButton>
    </BannerContainer>
  );
};

const MobileSimulatorDevice = ({
  bannerVisible = true,
  bannerColor = "gold",
  bannerHeader = "Welcome to the app",
  bannerSubheader = "Get started",
  bannerButtonText = "Get started",
  bannerState = "",
  notificationVisible = true,
  notificationHeader = "Push Notification",
  notificationSubheader = "Sub header for the notification",
  notificationCenterVisible = false,
  notificationCenterColor = "purple",
  notificationCenterIcon = "badge-percent",
  notificationCenterUnread = true,
  notificationCenterTimestamp = "2m",
  bottomSheetVisible = false,
  bottomSheetColor = "green",
  bottomSheetHeader = "Bottom Sheet",
  bottomSheetSubheader = "Sub header for the bottom sheet",
  bottomSheetPrimaryButtonText = "Close",
  bottomSheetIllustration = "security"
}) => {
  const [scale, setScale] = React.useState(1);

  React.useEffect(() => {
    const calculateScale = () => {
      const containerHeight = window.innerHeight - 160;
      const deviceHeight = 932;
      const newScale = Math.min(containerHeight / deviceHeight, 1);
      setScale(newScale);
    };

    calculateScale();
    window.addEventListener("resize", calculateScale);
    return () => window.removeEventListener("resize", calculateScale);
  }, []);

  return (
    <Device>
      <Frame style={{ "--device-scale": scale }}>
        <Screen>
          {!notificationVisible && !notificationCenterVisible && <Header />}
          {notificationVisible ? (
            <>
              <NotificationScreen />
              <NotificationContainer>
                <NotificationBubble>
                  <NotificationAppIcon
                    src="/images/simulator/propel-app-icon.png"
                    alt="App icon"
                  />
                  <NotificationContent>
                    <NotificationHeaderRow>
                      <NotificationHeader>
                        {notificationHeader}
                      </NotificationHeader>
                      <NotificationTimestamp>9:41 AM</NotificationTimestamp>
                    </NotificationHeaderRow>
                    <NotificationSubheader>
                      {notificationSubheader}
                    </NotificationSubheader>
                  </NotificationContent>
                </NotificationBubble>
              </NotificationContainer>
            </>
          ) : notificationCenterVisible ? (
            <>
              <NotificationCenterHeader />
              <NotificationCenterScreen>
                <NotificationCenterList>
                  <NotificationCenterItem>
                    <NotificationCenterIcon $color={notificationCenterColor}>
                      <FontAwesomeIcon
                        icon={NOTIFICATION_CENTER_ICONS[notificationCenterIcon] || faPercent}
                        style={{ fontSize: "16px", color: "#000" }}
                      />
                    </NotificationCenterIcon>
                    <NotificationCenterItemContent>
                      <NotificationCenterItemTitle>
                        {notificationHeader.replace(/ and /gi, " & ")}
                      </NotificationCenterItemTitle>
                      <NotificationCenterItemBody>
                        {(() => {
                          const text = notificationSubheader.replace(/ and /gi, " & ");
                          return text.length > 120
                            ? `${text.slice(0, 120)}...`
                            : text;
                        })()}
                      </NotificationCenterItemBody>
                    </NotificationCenterItemContent>
                    <NotificationCenterTimestamp>
                      <NotificationCenterTime>
                        {notificationCenterTimestamp}
                      </NotificationCenterTime>
                      {notificationCenterUnread && (
                        <NotificationCenterUnreadDot />
                      )}
                    </NotificationCenterTimestamp>
                  </NotificationCenterItem>
                  <NotificationCenterItem>
                    <NotificationCenterIcon $color="blue">
                      <FontAwesomeIcon
                        icon={faHouse}
                        style={{ fontSize: "16px", color: "#000" }}
                      />
                    </NotificationCenterIcon>
                    <NotificationCenterItemContent>
                      <NotificationCenterItemTitle>
                        Get ahead of winter bills
                      </NotificationCenterItemTitle>
                      <NotificationCenterItemBody>
                        Save on utilities, internet, & more before cold-weather costs set in.
                      </NotificationCenterItemBody>
                    </NotificationCenterItemContent>
                    <NotificationCenterTimestamp>
                      <NotificationCenterTime>
                        {notificationCenterTimestamp === "4h" ? "8h" : notificationCenterTimestamp === "8d" ? "12d" : "4h"}
                      </NotificationCenterTime>
                    </NotificationCenterTimestamp>
                  </NotificationCenterItem>
                  <NotificationCenterItem>
                    <NotificationCenterIcon $color="green">
                      <FontAwesomeIcon
                        icon={faLandmark}
                        style={{ fontSize: "16px", color: "#000" }}
                      />
                    </NotificationCenterIcon>
                    <NotificationCenterItemContent>
                      <NotificationCenterItemTitle>
                        Government shutdown update
                      </NotificationCenterItemTitle>
                      <NotificationCenterItemBody>
                        You will still get October SNAP benefits. Read more about the government shutdown.
                      </NotificationCenterItemBody>
                    </NotificationCenterItemContent>
                    <NotificationCenterTimestamp>
                      <NotificationCenterTime>
                        {notificationCenterTimestamp === "4h" ? "5d" : notificationCenterTimestamp === "8d" ? "18d" : "3d"}
                      </NotificationCenterTime>
                    </NotificationCenterTimestamp>
                  </NotificationCenterItem>
                </NotificationCenterList>
              </NotificationCenterScreen>
            </>
          ) : (
            <HomeFeed>
              {bannerVisible && (
                <Banner
                  color={bannerColor}
                  header={bannerHeader}
                  subheader={bannerSubheader}
                  buttonText={bannerButtonText}
                  state={bannerState}
                />
              )}
              <Wallet />
              <Offers />
            </HomeFeed>
          )}
          {!notificationVisible && !notificationCenterVisible && <Tabs />}
          {!notificationVisible && <HomeIndicator />}
          {bottomSheetVisible && (
            <BottomSheet
              color={bottomSheetColor}
              header={bottomSheetHeader}
              subheader={bottomSheetSubheader}
              primaryButtonText={bottomSheetPrimaryButtonText}
              illustration={bottomSheetIllustration}
            />
          )}
        </Screen>
        <FrameOverlay />
      </Frame>
    </Device>
  );
};

export default MobileSimulatorDevice;
